<template>
  <div class="resetPassword">
    <div class="center">
      <h1>{{ $t("resetPasswordPage.title") }}</h1>
      <p v-html="$t('resetPasswordPage.description')"></p>
      <form @submit.prevent="submit">
        <div>
          <text-field :label="$t('resetPasswordPage.emailLabel')" v-model="username" />
        </div>
        <button-default :disable="!username || submitting">{{ $t("resetPasswordPage.submitButton") }}</button-default>
      </form>
      <div v-show="message" class="warning">{{ message }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Auth } from "aws-amplify";
import TextField from "@/components/molecules/TextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";

interface Data {
  username: string;
  message: string;
  submitting: boolean;
}

interface Methods {
  submit(): Promise<void>;
}

interface Computed {
  from: string | undefined;
}

export default Vue.extend<Data, Methods, Computed>({
  // NOTE: metaタグの設定
  metaInfo() {
    if (process.env.NODE_ENV === "production") {
      return {};
    }
    return {
      meta: [
        {
          name: "robots",
          content: "none",
        },
      ],
    };
  },
  components: { TextField, ButtonDefault },
  data() {
    return {
      username: "",
      message: "",
      submitting: false,
    };
  },
  computed: {
    from() {
      return this.$route.query.from;
    },
  },
  created() {
    if (this.from === "nolanovel") {
      document.title = this.$t("resetPasswordPage.pageTitle.nolaNovel") as string;
    } else if (this.from === "default") {
      document.title = this.$t("resetPasswordPage.pageTitle.default") as string;
    }
  },
  methods: {
    async submit() {
      const { username } = this;

      try {
        this.submitting = true;

        if (this.from === "nolanovel") {
          // Nolaノベルのユーザーの場合はclientMetadataに「serviceName: Nolaノベル」を追加
          // そうすることで、本人認証のための確認コード送信メールにて、「Nolaノベル」から送信されたことになる
          await Auth.forgotPassword(username, { serviceName: "Nolaノベル" });
        } else if (this.from === "default") {
          await Auth.forgotPassword(username, { serviceName: "Nola関連サービス" });
        } else {
          await Auth.forgotPassword(username);
        }

        // クエリパラメータを動的に構築する
        const query = this.from ? { from: this.from } : {};

        // ルートを遷移する
        this.$router.push({
          name: "resetPasswordSubmit",
          params: { username },
          query,
        });
      } catch (err) {
        const error = err as any;
        this.submitting = false;
        console.error(error);
        if (error.code === "UserNotFoundException") {
          this.message = this.$t("resetPasswordPage.validation.userNotFound") as string;
        } else if (error.code === "LimitExceededException") {
          this.message = this.$t("resetPasswordPage.validation.limitExceeded") as string;
        } else {
          this.message = this.$t("resetPasswordPage.validation.genericError") as string;
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";
h1 {
  font-size: var(--font-size-xl);
  color: var(--color-text-normal);
}
p {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}
.resetPassword {
  position: relative;
  width: 100%;
  color: var(--color-text-on-fixed-bg);
}

.center {
  margin: 30px 10%;
  padding: 30px 50px;
  background-color: var(--color-surface-base);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  text-align: center;

  @include mq(sp) {
    margin: 50px 15px;
    padding: 0;
  }
}
.center form {
  padding: 15px 0;
  margin: auto;
  font-size: 16.1px;
  width: 50%;

  @include mq(sp) {
    width: 100%;
  }
}
.resetPassword button {
  padding: 15px 80px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  font-size: var(--font-size-md);
  color: var(--color-text-on-fixed-bg);
  margin-top: 30px;
}

.warning {
  border-radius: var(--radius-sm);
  padding: 20px;
  background-color: var(--color-surface-muted);
  border: 1px solid var(--color-warning);
  color: var(--color-warning);
}
</style>
